/** Journey只保存显式本地工作；业务合法性与权限仍在最新GET及原保存入口检查。 */
import { validQuoteJourneySession } from "./journey/agentJourneyView";
import type { CommercialForm } from "./commercialTermsForm";
import type { NreFormRow } from "./nreForm";
import type { PartLinkForm } from "./partLinkForm";
import { WORKSHEET_CARD_SELECTORS } from "./worksheetCardSections";
export type RawNumber = string | [string, string, string];
export type FieldRaw = {
  path: string;
  reportField: "qty" | "profit_ratio" | null;
  raw: RawNumber;
  remoteRaw: RawNumber;
  dirty: boolean;
  unsaved: boolean;
};
export type CommercialJourneyView = {
  form: CommercialForm;
  baseline: CommercialForm;
  open: boolean;
  freeName: boolean;
};
export type NreJourneyView = {
  rows: NreFormRow[];
  baseline: NreFormRow[];
  open: boolean;
};
export type PartLinkJourneyView = {
  tab: "pick" | "new";
  query: string;
  onlyCustomer: boolean;
  form: PartLinkForm | null;
  baseline: PartLinkForm | null;
};
export type QuoteFormJourney<T> = {
  id: string;
  label: string;
  restored?: T | null;
  onCapture?: (value: T | null) => void;
  approved?: boolean;
};
export type WorksheetJourneyOwner =
  | { kind: "quickquote" }
  | { kind: "wizard" }
  | { kind: "chat-card"; sessionId: string }
  | { kind: "project-line"; projectId: string; lineId: string };
export type WorksheetJourneyView = {
  quoteId: string;
  owner: WorksheetJourneyOwner;
  fields: FieldRaw[];
  ui: {
    scrollTop: number;
    cards: Record<string, boolean>;
    partLinkOpen: boolean;
  };
  commercial: CommercialJourneyView | null;
  nre: NreJourneyView | null;
  partLink: PartLinkJourneyView | null;
};
/** 一张报价工作表上登记的离开编辑器(切报价、关抽屉按这一组问);reject-note = 没发出的驳回理由 */
export const worksheetEditorIds = (id: string): readonly string[] =>
  ["numbers", "commercial", "nre", "part-link", "reject-note"].map(
    (key) => `worksheet:${id}:${key}`,
  );
const object = (v: unknown): v is Record<string, unknown> =>
  v !== null &&
  typeof v === "object" &&
  !Array.isArray(v) &&
  [Object.prototype, null].includes(Object.getPrototypeOf(v));
const exact = (v: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(v).length === keys.length &&
  Object.keys(v).every((key) => keys.includes(key));
const uuid = (v: unknown): v is string =>
  typeof v === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const text = (v: unknown, max = 200) =>
  typeof v === "string" && v.length <= max;
const raw = (v: unknown): v is RawNumber =>
  text(v, 100) ||
  (Array.isArray(v) && v.length === 3 && v.every((x) => text(x, 100)));
const nullableId = (v: unknown) => v === null || uuid(v);
const emptyId = (v: unknown) => v === "" || uuid(v);
export function isFieldRawArray(value: unknown): value is FieldRaw[] {
  if (!Array.isArray(value)) return false;
  const seen = new Set<string>();
  return value.every((v) => {
    if (
      !object(v) ||
      !exact(v, [
        "path",
        "reportField",
        "raw",
        "remoteRaw",
        "dirty",
        "unsaved",
      ]) ||
      typeof v.path !== "string" ||
      !raw(v.raw) ||
      !raw(v.remoteRaw) ||
      Array.isArray(v.raw) !== Array.isArray(v.remoteRaw) ||
      typeof v.dirty !== "boolean" ||
      typeof v.unsaved !== "boolean"
    )
      return false;
    if (
      v.reportField !== null
        ? !["qty", "profit_ratio"].includes(String(v.reportField)) ||
          v.path !== `$${v.reportField}` ||
          Array.isArray(v.raw)
        : // 工时底稿里只有精密特征数一项是工作表上的输入(main f01261f71),只放行这一条
          !(
            /^(part|blank|material|process|non_machining|surface|heat_treat|shipping|packaging|inspection|risk|overhead|outsourcing|finance|pricing|nre)\.[a-z0-9_.]{1,150}$/.test(
              v.path,
            ) || v.path === "time_basis.inputs.precision"
          ) ||
          v.path
            .split(".")
            .some((k) => ["__proto__", "prototype", "constructor"].includes(k))
    )
      return false;
    const key = `${v.reportField ?? "factor"}:${v.path}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function commercialForm(v: unknown): v is CommercialForm {
  const required = [
    "customer_id",
    "customer_name",
    "requested_due",
    "tax_choice",
    "payment_days",
    "valid_days",
    "incoterm",
    "drawing_no",
  ];
  const optional = [
    "lead_workdays",
    "exclusions_manual",
    "exclusions_unchecked",
  ];
  return (
    object(v) &&
    required.every((k) => Object.hasOwn(v, k)) &&
    Object.keys(v).every((k) => [...required, ...optional].includes(k)) &&
    nullableId(v.customer_id) &&
    text(v.customer_name) &&
    text(v.requested_due, 10) &&
    ["default", "inc", "ex"].includes(String(v.tax_choice)) &&
    text(v.payment_days, 100) &&
    text(v.valid_days, 100) &&
    text(v.incoterm) &&
    text(v.drawing_no, 100) &&
    (!Object.hasOwn(v, "lead_workdays") || text(v.lead_workdays, 100)) &&
    ["exclusions_manual", "exclusions_unchecked"].every(
      (k) =>
        !Object.hasOwn(v, k) ||
        (Array.isArray(v[k]) &&
          // Journey保存待修正原文；PATCH仍由原表单严格挡30项/60字。
          v[k].length <= 60 &&
          v[k].every((x: unknown) => text(x, 200))),
    )
  );
}
export function isCommercialJourneyView(
  v: unknown,
): v is CommercialJourneyView {
  return (
    object(v) &&
    exact(v, ["form", "baseline", "open", "freeName"]) &&
    commercialForm(v.form) &&
    commercialForm(v.baseline) &&
    typeof v.open === "boolean" &&
    typeof v.freeName === "boolean"
  );
}
function nreRow(v: unknown): v is NreFormRow {
  return (
    object(v) &&
    exact(v, [
      "name",
      "kind",
      "mode",
      "amount",
      "amortize_qty",
      "commitment",
    ]) &&
    text(v.name, 60) &&
    text(v.kind, 100) &&
    text(v.mode, 100) &&
    text(v.amount, 100) &&
    text(v.amortize_qty, 100) &&
    text(v.commitment, 200)
  );
}
export function isNreJourneyView(v: unknown): v is NreJourneyView {
  return (
    object(v) &&
    exact(v, ["rows", "baseline", "open"]) &&
    Array.isArray(v.rows) &&
    v.rows.every(nreRow) &&
    Array.isArray(v.baseline) &&
    v.baseline.every(nreRow) &&
    typeof v.open === "boolean"
  );
}
function partForm(v: unknown): v is PartLinkForm {
  return (
    object(v) &&
    exact(v, [
      "name",
      "drawing_no",
      "code",
      "material",
      "customerMode",
      "customer_id",
      "new_customer_code",
      "new_customer_name",
      "project_id",
    ]) &&
    text(v.name) &&
    text(v.drawing_no, 100) &&
    text(v.code, 50) &&
    text(v.material, 50) &&
    ["pick", "new"].includes(String(v.customerMode)) &&
    emptyId(v.customer_id) &&
    text(v.new_customer_code, 50) &&
    text(v.new_customer_name) &&
    emptyId(v.project_id)
  );
}
export function isPartLinkJourneyView(v: unknown): v is PartLinkJourneyView {
  return (
    object(v) &&
    exact(v, ["tab", "query", "onlyCustomer", "form", "baseline"]) &&
    ["pick", "new"].includes(String(v.tab)) &&
    text(v.query, 100) &&
    typeof v.onlyCustomer === "boolean" &&
    (v.form === null || partForm(v.form)) &&
    (v.baseline === null || partForm(v.baseline))
  );
}
export function readWorksheetJourney(
  page: unknown,
): WorksheetJourneyView | null {
  if (
    !object(page) ||
    page.key !== "worksheet" ||
    page.version !== 1 ||
    !Array.isArray(page.files) ||
    page.files.length ||
    !object(page.data)
  )
    return null;
  const v = page.data,
    o = v.owner,
    ui = v.ui;
  if (
    !exact(v, [
      "quoteId",
      "owner",
      "fields",
      "ui",
      "commercial",
      "nre",
      "partLink",
    ]) ||
    !uuid(v.quoteId) ||
    !object(o) ||
    !(o.kind === "chat-card"
      ? exact(o, ["kind", "sessionId"]) && validQuoteJourneySession(o.sessionId)
      : o.kind === "project-line"
        ? exact(o, ["kind", "projectId", "lineId"]) &&
          uuid(o.projectId) &&
          uuid(o.lineId)
        : exact(o, ["kind"]) &&
          ["quickquote", "wizard"].includes(String(o.kind))) ||
    !isFieldRawArray(v.fields) ||
    !object(ui) ||
    !exact(ui, ["scrollTop", "cards", "partLinkOpen"]) ||
    typeof ui.scrollTop !== "number" ||
    !Number.isFinite(ui.scrollTop) ||
    ui.scrollTop < 0 ||
    typeof ui.partLinkOpen !== "boolean" ||
    !object(ui.cards) ||
    !Object.entries(ui.cards).every(
      ([key, value]) =>
        Object.hasOwn(WORKSHEET_CARD_SELECTORS, key) &&
        typeof value === "boolean",
    ) ||
    !(v.commercial === null || isCommercialJourneyView(v.commercial)) ||
    !(v.nre === null || isNreJourneyView(v.nre)) ||
    !(v.partLink === null || isPartLinkJourneyView(v.partLink))
  )
    return null;
  return v as WorksheetJourneyView;
}
